import { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, SectionTag } from "../components/Reveal";
import SEO from "../components/SEO";
import ARModal from "../components/ARModal";
import { WORK_ITEMS, WORK_CATEGORIES, AR_LINKS } from "../content";

const Lightbox = ({ items, index, onClose, onNav }) => {
  const item = items[index];
  const next = useCallback(() => onNav((index + 1) % items.length), [index, items.length, onNav]);
  const prev = useCallback(() => onNav((index - 1 + items.length) % items.length), [index, items.length, onNav]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    let lock = false;
    const onWheel = (e) => {
      if (lock) return;
      lock = true;
      setTimeout(() => { lock = false; }, 500);
      if (e.deltaY > 0) next();
      else if (e.deltaY < 0) prev();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("wheel", onWheel, { passive: true });
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("wheel", onWheel);
      document.body.style.overflow = "";
    };
  }, [next, prev, onClose]);

  return (
    <div className="fixed inset-0 z-[100] bg-neutral-950/95 flex flex-col" data-testid="work-lightbox" onClick={onClose}>
      <div className="flex items-center justify-between px-6 py-4 text-white" onClick={(e) => e.stopPropagation()}>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-brand-red">{item.category}</p>
          <p className="text-sm font-semibold" data-testid="lightbox-title">{item.title}</p>
        </div>
        <button onClick={onClose} aria-label="Close gallery" data-testid="lightbox-close" className="p-2 hover:text-brand-red transition-colors">
          <X size={28} />
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 relative min-h-0" onClick={(e) => e.stopPropagation()}>
        <button onClick={prev} aria-label="Previous image" data-testid="lightbox-prev" className="absolute left-3 md:left-8 z-10 text-white/70 hover:text-brand-red p-2 transition-colors">
          <ChevronLeft size={44} />
        </button>
        <img
          key={item.id}
          src={item.image}
          alt={item.title}
          data-testid="lightbox-image"
          className="max-h-[76vh] max-w-[88vw] object-contain border border-neutral-800 bg-white"
        />
        <button onClick={next} aria-label="Next image" data-testid="lightbox-next" className="absolute right-3 md:right-8 z-10 text-white/70 hover:text-brand-red p-2 transition-colors">
          <ChevronRight size={44} />
        </button>
      </div>
      <p className="text-center text-neutral-500 text-xs uppercase tracking-widest py-5" onClick={(e) => e.stopPropagation()}>
        {index + 1} / {items.length}, scroll or use arrows to browse
      </p>
    </div>
  );
};

export default function Work() {
  const [active, setActive] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const [arExp, setArExp] = useState(null);
  const filtered = active === "All" ? WORK_ITEMS : WORK_ITEMS.filter((w) => w.category === active);

  return (
    <div data-testid="work-page">
      <SEO
        title="Work, 3D Renders, AR Experiences, VPV Dashboards & Brand Environments | The Next Design"
        description="Selected work by The Next Design: photoreal 3D product renders, AR experiences, Virtual Plant Visit dashboards, exhibition and branding projects for manufacturers."
      />
      <section className="border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <SectionTag>Work</SectionTag>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light uppercase tracking-tight leading-none" data-testid="work-heading">
              Proof, curated.<br /><span className="text-brand-red">Not crowded.</span>
            </h1>
            <p className="mt-8 text-lg text-neutral-600 max-w-2xl leading-relaxed">
              A selection of 3D renders, AR experiences, virtual plant visits and brand environments, the strongest proof of what strategy, technology and design can do for a manufacturer.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="flex flex-wrap gap-2 mb-14" data-testid="work-filters">
          {WORK_CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`work-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`px-5 py-2.5 text-xs font-bold uppercase tracking-widest border transition-colors ${
                active === c
                  ? "bg-neutral-950 text-white border-neutral-950"
                  : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-950"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {active === "AR & Interactive" && (
          <div className="mb-10 border border-neutral-900 p-6 md:p-8" data-testid="ar-live-strip">
            <p className="font-display text-xl font-semibold tracking-tight">Experience AR live</p>
            <p className="text-sm text-neutral-600 mt-1 mb-6">Click any product to launch the live experience, best on your phone.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {AR_LINKS.map((a, i) => (
                <button
                  key={a.url}
                  type="button"
                  onClick={() => setArExp(a)}
                  data-testid={`ar-live-link-${i}`}
                  className="border border-neutral-300 bg-white hover:bg-brand-red hover:border-brand-red hover:text-white px-4 py-3.5 text-xs font-bold uppercase tracking-widest transition-colors text-center"
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="work-grid">
          {filtered.map((w, i) => (
            <div key={w.id} className="bg-white border border-neutral-200 group flex flex-col hover:border-neutral-950 transition-colors" data-testid={`work-item-${w.id}`}>
              <button
                type="button"
                onClick={() => setLightbox(i)}
                aria-label={`Open ${w.title} full size`}
                data-testid={`work-item-image-${w.id}`}
                className="block w-full cursor-pointer"
              >
                <div className="aspect-[4/3] flex items-center justify-center p-8 overflow-hidden">
                  <img
                    src={w.image}
                    alt={w.title}
                    className="max-w-full max-h-full object-contain transition-transform duration-500 group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                </div>
              </button>
              <div className="p-5 border-t border-neutral-100 mt-auto">
                <p className="text-xs font-bold uppercase tracking-widest text-brand-red">{w.category}</p>
                <p className="mt-1 text-sm font-semibold text-neutral-900 group-hover:text-brand-red transition-colors">{w.title}</p>
              </div>
            </div>
          ))}
        </div>

        {lightbox !== null && (
          <Lightbox items={filtered} index={lightbox} onClose={() => setLightbox(null)} onNav={setLightbox} />
        )}
        {arExp && <ARModal experience={arExp} onClose={() => setArExp(null)} />}
      </section>
    </div>
  );
}
